import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, X, Newspaper, Eye, EyeOff } from 'lucide-react';
import { noticiasAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

export default function AdminNoticias() {
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNoticia, setEditingNoticia] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    resumo: '',
    conteudo: '',
    imagem_url: ''
  });
  const { toast } = useToast();

  const fetchNoticias = async () => {
    try {
      const res = await noticiasAPI.listar();
      setNoticias(res.data);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNoticias();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNoticia) {
        await noticiasAPI.atualizar(editingNoticia.id, formData);
        toast({ title: "Notícia atualizada com sucesso!" });
      } else {
        await noticiasAPI.criar(formData);
        toast({ title: "Notícia criada com sucesso!" });
      }
      
      setModalOpen(false);
      setEditingNoticia(null);
      setFormData({ titulo: '', resumo: '', conteudo: '', imagem_url: '' });
      fetchNoticias();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  const handleEdit = (noticia) => {
    setEditingNoticia(noticia);
    setFormData({
      titulo: noticia.titulo,
      resumo: noticia.resumo || '',
      conteudo: noticia.conteudo,
      imagem_url: noticia.imagem_url || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta notícia?')) return;
    try {
      await noticiasAPI.deletar(id);
      toast({ title: "Notícia excluída com sucesso!" });
      fetchNoticias();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="admin-noticias">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Notícias</h1>
          <p className="text-slate-600 mt-1">Gerencie as notícias e atualizações</p>
        </div>
        <button
          onClick={() => {
            setEditingNoticia(null);
            setFormData({ titulo: '', resumo: '', conteudo: '', imagem_url: '' });
            setModalOpen(true);
          }}
          data-testid="add-noticia-btn"
          className="bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-800 transition-colors"
        >
          <Plus size={20} />
          Nova Notícia
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : noticias.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Newspaper className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhuma notícia cadastrada</h3>
          <p className="text-slate-500">Clique em "Nova Notícia" para começar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {noticias.map((noticia, index) => (
            <motion.div
              key={noticia.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-6 shadow-sm flex gap-6"
              data-testid={`noticia-card-${noticia.id}`}
            >
              {noticia.imagem_url && (
                <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={noticia.imagem_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${noticia.imagem_url}` : noticia.imagem_url}
                    alt={noticia.titulo}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900">
                      {noticia.titulo}
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {new Date(noticia.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(noticia)}
                      data-testid={`edit-noticia-${noticia.id}`}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(noticia.id)}
                      data-testid={`delete-noticia-${noticia.id}`}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                
                {noticia.resumo && (
                  <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                    {noticia.resumo}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="noticia-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="font-heading text-xl font-bold">
                {editingNoticia ? 'Editar Notícia' : 'Nova Notícia'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Título *</label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  required
                  data-testid="modal-input-titulo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Título da notícia"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Resumo</label>
                <input
                  type="text"
                  name="resumo"
                  value={formData.resumo}
                  onChange={handleChange}
                  data-testid="modal-input-resumo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Breve descrição..."
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Conteúdo *</label>
                <textarea
                  name="conteudo"
                  value={formData.conteudo}
                  onChange={handleChange}
                  required
                  rows={8}
                  data-testid="modal-input-conteudo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                  placeholder="Conteúdo completo da notícia..."
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">URL da Imagem</label>
                <input
                  type="text"
                  name="imagem_url"
                  value={formData.imagem_url}
                  onChange={handleChange}
                  data-testid="modal-input-imagem"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="modal-submit-btn"
                  className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800"
                >
                  {editingNoticia ? 'Salvar' : 'Publicar'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
